import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  ArrowUpTrayIcon,
  FolderPlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  copyStorageObject,
  createStorageFolder,
  deleteStorageObjects,
  fetchStorageDownloadUrl,
  listStorageBuckets,
  listStorageObjects,
  StorageBucket,
  StorageObjectItem,
  StorageObjectListResult,
  uploadStorageObject,
} from '../../services/storageApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';

function formatBytes(size?: number | null): string {
  if (size == null || Number.isNaN(size)) return '—';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function buildBreadcrumbSegments(
  prefix: string,
  companyPrefix: string,
): { label: string; prefix: string }[] {
  const segments: { label: string; prefix: string }[] = [
    { label: 'Raiz do bucket', prefix: '' },
  ];

  const parts = prefix.split('/').filter(Boolean);
  let current = '';
  for (const part of parts) {
    current = `${current}${part}/`;
    const isCompanyRoot = companyPrefix.length > 0 && current === companyPrefix;
    segments.push({
      label: isCompanyRoot ? 'Pasta da empresa' : part,
      prefix: current,
    });
  }

  return segments;
}

export function StorageObjectsPanel() {
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('storage');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [buckets, setBuckets] = useState<StorageBucket[]>([]);
  const [selectedBucket, setSelectedBucket] = useState('');
  const [listResult, setListResult] = useState<StorageObjectListResult | null>(null);
  const [currentPrefix, setCurrentPrefix] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const items = listResult?.items ?? [];
  const companyPrefix = listResult?.companyPrefix ?? '';
  const effectivePrefix = currentPrefix ?? '';

  const breadcrumbs = useMemo(
    () => buildBreadcrumbSegments(effectivePrefix, companyPrefix),
    [effectivePrefix, companyPrefix],
  );

  const loadBuckets = useCallback(async (): Promise<void> => {
    try {
      const result = await listStorageBuckets();
      setBuckets(result);
      setSelectedBucket((current) => {
        if (current && result.some((bucket) => bucket.name === current)) {
          return current;
        }
        const defaultBucket = result.find((bucket) => bucket.isDefault);
        return defaultBucket?.name ?? result[0]?.name ?? '';
      });
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar buckets.'));
    }
  }, []);

  const loadObjects = useCallback(
    async (prefix?: string | null): Promise<void> => {
      setLoading(true);
      setSelectedKeys(new Set());
      try {
        const result = await listStorageObjects({
          bucket: selectedBucket || undefined,
          prefix: prefix === undefined || prefix === null ? undefined : prefix,
          recursive: false,
        });
        setListResult(result);
        setCurrentPrefix(result.prefix ?? '');
      } catch (error) {
        toast.error(getRequestErrorMessage(error, 'Falha ao listar objetos.'));
        setListResult(null);
      } finally {
        setLoading(false);
      }
    },
    [selectedBucket],
  );

  useEffect(() => {
    void loadBuckets();
  }, [loadBuckets]);

  useEffect(() => {
    if (!selectedBucket && buckets.length === 0) {
      return;
    }
    void loadObjects('');
  }, [selectedBucket, buckets.length, loadObjects]);

  function toggleKey(key: string): void {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function toggleAll(): void {
    if (selectedKeys.size === items.length) {
      setSelectedKeys(new Set());
      return;
    }
    setSelectedKeys(new Set(items.map((item) => item.key)));
  }

  async function handleUpload(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !canWrite) return;

    setUploading(true);
    try {
      await uploadStorageObject({
        file,
        bucket: selectedBucket || undefined,
        prefix: effectivePrefix,
      });
      toast.success('Arquivo enviado.');
      await loadObjects(effectivePrefix);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao enviar arquivo.'));
    } finally {
      setUploading(false);
    }
  }

  async function handleCreateFolder(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const name = folderName.trim();
    if (!name) {
      toast.error('Informe o nome da pasta.');
      return;
    }

    setCreatingFolder(true);
    try {
      await createStorageFolder({
        bucketName: selectedBucket || undefined,
        prefix: effectivePrefix,
        folderName: name,
      });
      setFolderName('');
      toast.success('Pasta criada.');
      await loadObjects(effectivePrefix);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao criar pasta.'));
    } finally {
      setCreatingFolder(false);
    }
  }

  async function handleDeleteSelected(): Promise<void> {
    if (!canWrite || selectedKeys.size === 0) return;
    if (
      !window.confirm(
        `Excluir ${selectedKeys.size} item(ns) selecionado(s)? Esta ação não pode ser desfeita.`,
      )
    ) {
      return;
    }

    setBusy(true);
    try {
      await deleteStorageObjects({
        bucketName: selectedBucket || undefined,
        keys: Array.from(selectedKeys),
      });
      toast.success('Itens excluídos.');
      await loadObjects(effectivePrefix);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao excluir objetos.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleDownload(item: StorageObjectItem): Promise<void> {
    if (item.isFolder) return;
    try {
      const result = await fetchStorageDownloadUrl({
        key: item.key,
        bucket: selectedBucket || undefined,
      });
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao gerar URL de download.'));
    }
  }

  async function handleRenameOrMove(item: StorageObjectItem): Promise<void> {
    if (!canWrite || item.isFolder) return;

    const destinationKey = window.prompt(
      'Novo caminho completo (renomear/mover). A origem será removida após a cópia.',
      item.key,
    );
    if (!destinationKey || destinationKey.trim() === item.key) {
      return;
    }

    setBusy(true);
    try {
      await copyStorageObject({
        bucketName: selectedBucket || undefined,
        sourceKey: item.key,
        destinationKey: destinationKey.trim(),
        deleteSource: true,
      });
      toast.success('Objeto movido/renomeado.');
      await loadObjects(effectivePrefix);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao mover/renomear objeto.'));
    } finally {
      setBusy(false);
    }
  }

  function openFolder(item: StorageObjectItem): void {
    if (!item.isFolder) return;
    const nextPrefix = item.key.endsWith('/') ? item.key : `${item.key}/`;
    void loadObjects(nextPrefix);
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Objetos</h3>
            <p className="mt-1 text-sm text-slate-500">
              Navegue pelos arquivos do bucket selecionado. A pasta da empresa fica em{' '}
              <span className="font-mono text-xs">{companyPrefix || '…/'}</span>.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Bucket
            </label>
            <select
              value={selectedBucket}
              onChange={(event) => setSelectedBucket(event.target.value)}
              className="h-9 rounded-lg border border-slate-300 px-3 text-sm"
            >
              {buckets.length === 0 ? <option value="">Sem buckets</option> : null}
              {buckets.map((bucket) => (
                <option key={bucket.name} value={bucket.name}>
                  {bucket.name}
                  {bucket.isDefault ? ' (padrão)' : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="erp-btn-secondary erp-btn-sm h-9"
              onClick={() => void loadObjects(effectivePrefix)}
            >
              Atualizar
            </button>
          </div>
        </div>

        <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-slate-600">
          {breadcrumbs.map((segment, index) => (
            <span key={`${segment.prefix}-${segment.label}`} className="inline-flex items-center gap-1">
              {index > 0 ? <span className="text-slate-400">/</span> : null}
              <button
                type="button"
                className="rounded px-1.5 py-0.5 hover:bg-slate-100 hover:text-slate-900"
                onClick={() => void loadObjects(segment.prefix)}
              >
                {segment.label}
              </button>
            </span>
          ))}
        </nav>

        {canWrite ? (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <form onSubmit={(event) => void handleCreateFolder(event)} className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={folderName}
                onChange={(event) => setFolderName(event.target.value)}
                placeholder="Nova pasta"
                className="h-9 rounded-lg border border-slate-300 px-3 text-sm"
              />
              <button
                type="submit"
                className="erp-btn-secondary erp-btn-sm h-9 gap-1.5"
                disabled={creatingFolder || busy}
              >
                <FolderPlusIcon className="h-4 w-4" aria-hidden />
                {creatingFolder ? 'Criando...' : 'Criar pasta'}
              </button>
            </form>

            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(event) => void handleUpload(event)}
            />
            <button
              type="button"
              className="erp-btn-primary erp-btn-sm h-9 gap-1.5"
              disabled={uploading || busy}
              onClick={() => fileInputRef.current?.click()}
            >
              <ArrowUpTrayIcon className="h-4 w-4" aria-hidden />
              {uploading ? 'Enviando...' : 'Enviar arquivo'}
            </button>

            <button
              type="button"
              className="erp-btn-danger erp-btn-sm h-9 gap-1.5"
              disabled={busy || selectedKeys.size === 0}
              onClick={() => void handleDeleteSelected()}
            >
              <TrashIcon className="h-4 w-4" aria-hidden />
              Excluir selecionados
            </button>
          </div>
        ) : (
          <p className="mb-4 text-xs text-slate-500">
            Upload, pastas, exclusão e mover exigem permissão de escrita em Armazenamento.
          </p>
        )}

        {loading ? (
          <LoadingSpinner label="Carregando objetos..." />
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhum objeto neste prefixo.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-2">
                    <input
                      type="checkbox"
                      checked={items.length > 0 && selectedKeys.size === items.length}
                      onChange={toggleAll}
                      aria-label="Selecionar todos"
                    />
                  </th>
                  <th className="px-2 py-2 font-medium">Nome</th>
                  <th className="px-2 py-2 font-medium">Tipo</th>
                  <th className="px-2 py-2 font-medium">Tamanho</th>
                  <th className="px-2 py-2 font-medium">Modificado</th>
                  <th className="px-2 py-2 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.key} className="border-b border-slate-100">
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(item.key)}
                        onChange={() => toggleKey(item.key)}
                        aria-label={`Selecionar ${item.name}`}
                      />
                    </td>
                    <td className="px-2 py-2">
                      {item.isFolder ? (
                        <button
                          type="button"
                          className="font-medium text-blue-700 hover:underline"
                          onClick={() => openFolder(item)}
                        >
                          {item.name}/
                        </button>
                      ) : (
                        <span className="font-mono text-slate-800">{item.name}</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-slate-600">
                      {item.isFolder ? 'Pasta' : 'Arquivo'}
                    </td>
                    <td className="px-2 py-2 text-slate-600">
                      {item.isFolder ? '—' : formatBytes(item.sizeBytes)}
                    </td>
                    <td className="px-2 py-2 text-slate-600">
                      {item.lastModifiedUtc
                        ? new Date(item.lastModifiedUtc).toLocaleString('pt-BR')
                        : '—'}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <div className="inline-flex flex-wrap justify-end gap-1">
                        {!item.isFolder ? (
                          <button
                            type="button"
                            className="erp-btn-secondary erp-btn-sm"
                            onClick={() => void handleDownload(item)}
                          >
                            Download
                          </button>
                        ) : null}
                        {canWrite && !item.isFolder ? (
                          <button
                            type="button"
                            className="erp-btn-secondary erp-btn-sm"
                            disabled={busy}
                            onClick={() => void handleRenameOrMove(item)}
                          >
                            Mover/renomear
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
